import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useColorModeValue,
  Heading,
  Card,
  CardBody,
  Input,
} from '@chakra-ui/react';
import { AddIcon, SearchIcon } from '@chakra-ui/icons';
import { useAuth } from '../contexts/AuthContext';
import { timesheetAPI, projectsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const TimesheetList: React.FC = () => {
  const { user } = useAuth();
  const [myTimesheets, setMyTimesheets] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user?.associateId) {
      timesheetAPI.getTimesheets(user.associateId).then((res: any) => setMyTimesheets(res.data));
      projectsAPI.getAll().then((res: any) => {
        const assigned = res.data.filter((p: any) => Array.isArray(p.associates) && p.associates.includes(user.associateId));
        setProjects(assigned);
      });
    }
  }, [user]);

  // Format week range as 'DD-MM to DD-MM'
  function formatWeek(start: string, end: string): string {
    if (!start || !end) return '-';
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return `${start} to ${end}`;
    const format = (date: Date) => `${date.getDate().toString().padStart(2,'0')}-${(date.getMonth()+1).toString().padStart(2,'0')}`;
    return `${format(startDate)} to ${format(endDate)}`;
  }

  return (
    <Box>
      <Box mb={8}>
        <Heading size="lg" color="gray.800" mb={2}>
          Timesheet Records
        </Heading>
        <Text color="gray.600">
          View and manage your weekly timesheet submissions
        </Text>
      </Box>

      <Box mb={6}>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="primary"
          onClick={() => navigate('/submit-timesheet')}
          className="btn-primary"
        >
          Add Timesheet
        </Button>
      </Box>

      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody p={0}>
          <Box overflowX="auto">
            <Table variant="simple" minWidth="700px">
              <Thead>
                <Tr>
                  <Th className="table-header">Timesheet ID</Th>
                  <Th className="table-header">Project</Th>
                  <Th className="table-header">Week</Th>
                  {/* <Th className="table-header">Hours (Mon-Sun)</Th> */}
                  <Th className="table-header">Total Hours</Th>
                  <Th className="table-header">Status</Th>
                  <Th className="table-header">Note</Th>
                </Tr>
              </Thead>
              <Tbody>
                {myTimesheets.length === 0 ? (
                  <Tr>
                    <Td colSpan={7} textAlign="center">
                      <Text color="gray.500">No timesheet records found</Text>
                    </Td>
                  </Tr>
                ) : (
                  myTimesheets.map((ts, idx) => {
                    const project = projects.find(p => String(p._id) === String(ts.projectId));
                    // Status badge color logic
                    let statusColor = 'gray';
                    const status = (ts.status || 'pending').toUpperCase();
                    if (status === 'PENDING') statusColor = 'yellow';
                    else if (status === 'APPROVED') statusColor = 'green';
                    else if (status === 'REJECTED') statusColor = 'red';
                    return (
                      <Tr key={idx}>
                        <Td className="table-cell">
                          <Text fontWeight="bold" color="gray.700">{ts._id}</Text>
                        </Td>
                        <Td className="table-cell">
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium">{project ? project.name : ts.projectId}</Text>
                            <Text fontSize="sm" color="gray.500">ID:{ts.projectId}</Text>
                          </VStack>
                        </Td>
                        <Td className="table-cell">{formatWeek(ts.weekStart, ts.weekEnd)}</Td>
                        {/* <Td className="table-cell">{ts.dailyHours.join(', ')}</Td> */}
                        <Td className="table-cell">
                          <Box position="relative" display="inline-block">
                            <Text
                              fontWeight="bold"
                              cursor="pointer"
                              title={ts.dailyHours && ts.dailyHours.length === 7 ?
                                ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, i) => `${day}: ${ts.dailyHours[i]}`).join("\n")
                                : undefined
                              }
                              _hover={{ textDecoration: 'underline', color: 'blue.500' }}
                            >
                              {typeof ts.totalHours === 'number' ? ts.totalHours : ts.dailyHours.reduce((sum: number, h: number) => sum + h, 0)}
                            </Text>
                            <Text fontSize="xs" color="gray.500" mt={1} textAlign="center">Hover for details</Text>
                          </Box>
                        </Td>
                        <Td className="table-cell">
                          <Badge colorScheme={statusColor} variant="subtle">
                            {status}
                          </Badge>
                        </Td>
                        <Td className="table-cell">{ts.note || '-'}</Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
      </Card>
    </Box>
  );
};

export default TimesheetList;
